import { useState, useEffect, useRef, useCallback, useMemo } from "react";

const PROVIDER_COLORS = { openai: "#10B981", anthropic: "#F5DF4D", groq: "#3B82F6", openrouter: "#A78BFA" };
const DEFAULT_COLOR = "#94A3B8";
const MAX_VISIBLE_NODES = 40;

function forceSimulation(nodes, edges, width, height, iterations) {
  // Fix #2: Scale iterations down for large graphs
  const iters = iterations || Math.min(80, Math.max(30, 120 - nodes.length));
  const positions = nodes.map((_, i) => ({
    x: width / 2 + (Math.cos(i * 2.4) * width * 0.3) + (Math.random() - 0.5) * 60,
    y: height / 2 + (Math.sin(i * 2.4) * height * 0.3) + (Math.random() - 0.5) * 60,
    vx: 0, vy: 0,
  }));

  const idxMap = {};
  nodes.forEach((n, i) => { idxMap[n.id] = i; });

  for (let iter = 0; iter < iters; iter++) {
    const alpha = 1 - iter / iters;
    const repulsion = 8000 * alpha;
    const attraction = 0.005 * alpha;
    const centerPull = 0.01 * alpha;

    // Repulsion between all pairs
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        let dx = positions[i].x - positions[j].x;
        let dy = positions[i].y - positions[j].y;
        let dist = Math.sqrt(dx * dx + dy * dy) || 1;
        let force = repulsion / (dist * dist);
        let fx = (dx / dist) * force;
        let fy = (dy / dist) * force;
        positions[i].vx += fx;
        positions[i].vy += fy;
        positions[j].vx -= fx;
        positions[j].vy -= fy;
      }
    }

    // Attraction along edges
    for (const edge of edges) {
      const si = idxMap[edge.source_agent_id];
      const ti = idxMap[edge.target_agent_id];
      if (si === undefined || ti === undefined) continue;
      let dx = positions[ti].x - positions[si].x;
      let dy = positions[ti].y - positions[si].y;
      let dist = Math.sqrt(dx * dx + dy * dy) || 1;
      let force = (dist - 120) * attraction;
      positions[si].vx += (dx / dist) * force;
      positions[si].vy += (dy / dist) * force;
      positions[ti].vx -= (dx / dist) * force;
      positions[ti].vy -= (dy / dist) * force;
    }

    // Center gravity
    for (let i = 0; i < positions.length; i++) {
      positions[i].vx += (width / 2 - positions[i].x) * centerPull;
      positions[i].vy += (height / 2 - positions[i].y) * centerPull;
    }

    // Apply velocity with damping
    for (let i = 0; i < positions.length; i++) {
      positions[i].x += positions[i].vx * 0.6;
      positions[i].y += positions[i].vy * 0.6;
      positions[i].vx *= 0.7;
      positions[i].vy *= 0.7;
      // Bound
      positions[i].x = Math.max(50, Math.min(width - 50, positions[i].x));
      positions[i].y = Math.max(40, Math.min(height - 40, positions[i].y));
    }
  }

  return positions;
}

export default function NetworkGraph({ nodes, edges, onSelectAgent, selectedId, activeAgents = [] }) {
  const containerRef = useRef(null);
  const [positions, setPositions] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [dims, setDims] = useState({ width: 800, height: 400 });
  const [showAll, setShowAll] = useState(false);

  // Fix #2: Limit visible nodes for large graphs
  const isLargeGraph = nodes.length > MAX_VISIBLE_NODES;
  const visibleNodes = useMemo(
    () => isLargeGraph && !showAll ? nodes.slice(0, MAX_VISIBLE_NODES) : nodes,
    [isLargeGraph, showAll, nodes]
  );
  const visibleIds = useMemo(() => new Set(visibleNodes.map(n => n.id)), [visibleNodes]);
  const visibleEdges = useMemo(
    () => edges.filter(e => visibleIds.has(e.source_agent_id) && visibleIds.has(e.target_agent_id)),
    [edges, visibleIds]
  );

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDims({ width: rect.width, height: Math.max(rect.height, 350) });
    }
  }, []);

  useEffect(() => {
    if (visibleNodes.length === 0) return;
    const pos = forceSimulation(visibleNodes, visibleEdges, dims.width, dims.height);
    setPositions(pos);
  }, [visibleNodes, visibleEdges, dims]);

  const idxMap = {};
  visibleNodes.forEach((n, i) => { idxMap[n.id] = i; });

  const activeSet = new Set(activeAgents);
  const hasActive = activeSet.size > 0;

  return (
    <div ref={containerRef} data-testid="agent-network-graph" className="w-full h-full min-h-[350px] relative">
      <svg width={dims.width} height={dims.height} className="absolute inset-0">
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="activeGlow">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Edges */}
        {visibleEdges.map((edge, i) => {
          const si = idxMap[edge.source_agent_id];
          const ti = idxMap[edge.target_agent_id];
          if (si === undefined || ti === undefined || !positions[si] || !positions[ti]) return null;

          const srcActive = activeSet.has(edge.source_agent_id);
          const tgtActive = activeSet.has(edge.target_agent_id);
          const edgeActive = srcActive && tgtActive;
          const isHighlighted = edgeActive || hoveredId === edge.source_agent_id || hoveredId === edge.target_agent_id
            || selectedId === edge.source_agent_id || selectedId === edge.target_agent_id;
          const dimmed = hasActive && !srcActive && !tgtActive;

          return (
            <line key={`edge-${i}`}
              x1={positions[si].x} y1={positions[si].y}
              x2={positions[ti].x} y2={positions[ti].y}
              stroke={edgeActive ? "#E5FF00" : isHighlighted ? "#E5FF00" : "#273142"}
              strokeWidth={edgeActive ? 2.5 : isHighlighted ? 2 : 1}
              strokeDasharray={edgeActive ? "6 4" : isHighlighted ? "none" : "4 4"}
              opacity={dimmed ? 0.12 : isHighlighted ? 0.8 : 0.4}
              style={edgeActive ? { animation: "edgeFlow 0.8s linear infinite" } : undefined}
              className="transition-all duration-300"
            />
          );
        })}

        {/* Nodes */}
        {visibleNodes.map((node, i) => {
          if (!positions[i]) return null;
          const color = PROVIDER_COLORS[node.provider_type] || DEFAULT_COLOR;
          const isLit = activeSet.has(node.id);
          const isSelected = selectedId === node.id;
          const isHovered = hoveredId === node.id;
          const isActive = isSelected || isHovered || isLit;
          const dimmed = hasActive && !isLit && !isHovered;
          const radius = isLit ? 28 : isActive ? 28 : 22;
          const edgeCount = visibleEdges.filter(e => e.source_agent_id === node.id || e.target_agent_id === node.id).length;

          return (
            <g key={node.id}
              onClick={() => onSelectAgent(node)}
              onMouseEnter={() => setHoveredId(node.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="cursor-pointer"
              data-testid={`network-node-${node.id}`}
              opacity={dimmed ? 0.3 : 1}
              style={{ transition: "opacity 0.4s ease" }}
            >
              {/* Active pulse rings */}
              {isLit && (
                <>
                  <circle cx={positions[i].x} cy={positions[i].y} r={radius + 6}
                    fill="none" stroke="#E5FF00" strokeWidth="2"
                    style={{ animation: "nodeRingPulse 1.5s ease-in-out infinite" }}
                    filter="url(#activeGlow)" />
                  <circle cx={positions[i].x} cy={positions[i].y} r={radius + 4}
                    fill="#E5FF00" opacity="0"
                    style={{ animation: "nodePulse 2s ease-out infinite" }} />
                </>
              )}

              {/* Hover glow ring (non-active) */}
              {!isLit && isActive && (
                <circle cx={positions[i].x} cy={positions[i].y} r={radius + 6}
                  fill="none" stroke={color} strokeWidth="1" opacity="0.3" filter="url(#glow)" />
              )}

              {/* Node circle */}
              <circle cx={positions[i].x} cy={positions[i].y} r={radius}
                fill={isLit ? "#E5FF0018" : isActive ? color + "30" : "#111620"}
                stroke={isLit ? "#E5FF00" : isActive ? color : "#273142"}
                strokeWidth={isLit ? 2.5 : isActive ? 2 : 1}
                className="transition-all duration-200"
              />

              {/* Icon dot */}
              <circle cx={positions[i].x} cy={positions[i].y - 4} r={isLit ? 5 : 4}
                fill={isLit ? "#E5FF00" : color} opacity={isActive ? 1 : 0.6} />

              {/* Name label */}
              <text x={positions[i].x} y={positions[i].y + 8}
                textAnchor="middle" fontSize={isActive ? "9" : "8"}
                fill={isLit ? "#E5FF00" : isActive ? "#FFFFFF" : "#94A3B8"}
                fontFamily="'IBM Plex Sans', sans-serif" fontWeight={isLit ? "700" : isActive ? "600" : "400"}
                className="pointer-events-none select-none"
              >
                {node.name.length > 12 ? node.name.slice(0, 11) + "…" : node.name}
              </text>

              {/* Edge count badge */}
              {edgeCount > 0 && !isLit && (
                <>
                  <circle cx={positions[i].x + radius - 4} cy={positions[i].y - radius + 4} r={7}
                    fill="#1C2433" stroke="#273142" strokeWidth="1" />
                  <text x={positions[i].x + radius - 4} y={positions[i].y - radius + 7}
                    textAnchor="middle" fontSize="7" fill="#94A3B8"
                    fontFamily="'JetBrains Mono', monospace"
                    className="pointer-events-none select-none">
                    {edgeCount}
                  </text>
                </>
              )}

              {/* "ACTIVE" badge for lit nodes */}
              {isLit && (
                <>
                  <rect x={positions[i].x - 16} y={positions[i].y - radius - 14} width={32} height={12} rx={6}
                    fill="#E5FF00" />
                  <text x={positions[i].x} y={positions[i].y - radius - 6}
                    textAnchor="middle" fontSize="6" fill="#080B10" fontWeight="700"
                    fontFamily="'JetBrains Mono', monospace"
                    className="pointer-events-none select-none">ACTIVE</text>
                </>
              )}

              {/* Free/paid badge */}
              {node.pricing_mode === "paid" && !isLit && (
                <>
                  <circle cx={positions[i].x - radius + 4} cy={positions[i].y - radius + 4} r={7}
                    fill="#E5FF00" stroke="none" />
                  <text x={positions[i].x - radius + 4} y={positions[i].y - radius + 7}
                    textAnchor="middle" fontSize="7" fill="#080B10" fontWeight="700"
                    className="pointer-events-none select-none">$</text>
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hoveredId && (() => {
        const idx = idxMap[hoveredId];
        const node = visibleNodes[idx];
        const pos = positions[idx];
        if (!node || !pos) return null;
        const isLit = activeSet.has(hoveredId);
        return (
          <div className="absolute pointer-events-none z-10 bg-[#111620] border border-[#273142] rounded-lg px-3 py-2 shadow-xl"
            style={{ left: pos.x + 35, top: pos.y - 20, maxWidth: 200, borderColor: isLit ? "#E5FF00" : undefined }}>
            <p className="text-xs font-semibold text-white">{node.name}</p>
            {isLit && <p className="text-[10px] text-[#E5FF00] font-mono mt-0.5">Processing query...</p>}
            <p className="text-[10px] text-[#94A3B8] line-clamp-2 mt-0.5">{node.description}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-[10px] text-[#475569] uppercase">{node.provider_type}</span>
              <span className="font-mono text-[10px] text-[#475569]">{node.usage_count} uses</span>
              <span className={`font-mono text-[10px] ${node.pricing_mode === "free" ? "text-[#10B981]" : "text-[#E5FF00]"}`}>
                {node.pricing_mode}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Fix #2: Show more button for large graphs */}
      {isLargeGraph && !showAll && (
        <button onClick={() => setShowAll(true)} data-testid="graph-show-more"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 font-mono text-[10px] text-[#E5FF00] bg-[#111620]/90 border border-[#E5FF00]/30 px-3 py-1.5 rounded-full hover:bg-[#E5FF00]/10 transition-colors z-10">
          Showing {MAX_VISIBLE_NODES} of {nodes.length} — Show All
        </button>
      )}
      {isLargeGraph && showAll && (
        <button onClick={() => setShowAll(false)} data-testid="graph-show-less"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 font-mono text-[10px] text-[#475569] bg-[#111620]/90 border border-[#273142] px-3 py-1.5 rounded-full hover:text-white transition-colors z-10">
          Showing all {nodes.length} — Collapse
        </button>
      )}
    </div>
  );
}
